import React, { useState, useEffect, useCallback } from "react";
import { IEventSummary } from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";

import { apiService } from "src/services/api.service";

import type { ColDef } from "ag-grid-community";
import { themeQuartz } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";

import { ActionsRenderer } from "./ActionsRenderer";

import styles from "./AddOnsTab.module.scss";

import { toast } from "src/components/Toast/ToastManager";
import Input from "src/components/Input/Input";
import Button from "src/components/Button/Button";
import { Modal, useModal } from "src/components/Modal/Modal";
import TabContent, { useTabContent } from "src/components/TabContent/TabContent";
// import { fi } from "date-fns/locale";

interface IProps {
  event: IEventSummary,
  currentTab: number,
  tabIndex: number
}

interface IRow {
  title: string,
  description: string,
  price: string,
  quantity: string,
  remaining: string,
  received: string,
  actions: any
}

interface IRowPurchases {
    title: string,
    email: string,
    name: string,
    quantity: string,
    total_amount: string,
    currency: string
}

const AddOnsTab: React.FC<IProps> = (props) => {
    const { isShown, toggle } = useModal();
    const [loaded, setLoaded] = useState<boolean>(true);

    const [title, setTitle] = useState<string>("");
    const [description, setDescription] = useState<string>("");
    const [price, setPrice] = useState<string>("");
    const [quantity, setQuantity] = useState<string>("");

    const { activeTab, setTab } = useTabContent();
    

    const theme = themeQuartz.withParams({
        fontFamily: "Roboto",
        headerFontFamily: "Roboto",
        cellFontFamily: "Roboto"
    });

    const getEventAddonPurchases = useCallback((id: string) => {
        // const _query:any = {
        //     sort: [],
        //     query: {
        //         $and: []
        //     }
        // };
        
        apiService.getEventAddOnRegistrations(id).then((response) => {
            let rows:IRowPurchases[] = [];

            for (let i in response) {
                let row:any = response[i];

                const formatter = new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: props.event!.currency!.toUpperCase() || "USD",
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                });

                rows.push({
                    title: row.add_on_title,
                    email: row.email,
                    name: row.display_name,
                    quantity: row.quantity,
                    total_amount: formatter.format(row.fee),
                    currency: props.event!.currency!.toUpperCase() || "USD",
                });

                setRowPurchasesData(rows);
            }
        });
    }, [props.event]);

    const getEventAddOns = useCallback((id: string) => {
        let query:any = {
            sort: [],
            query: {
                $and: []
            }
        };

        apiService.getEventAddOns(id, query).then((response) => {
            let rows:IRow[] = [];

            for (let i in response) {
                let row:any = response[i];

                const formatter = new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: props.event!.currency!.toUpperCase() || "USD",
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                });

                rows.push({
                    title: row.title,
                    description: row.description,
                    price: formatter.format(row.fee),
                    quantity: row.available_quantity,
                    remaining: (row.available_quantity - row.purchased_quantity).toString(),
                    received: formatter.format((row.fee * row.purchased_quantity)),
                    actions: {
                        addon: row,
                        event: props.event
                    }
                });
            }

            setRowData(rows);
        });
    }, [props.event]);

    const createAddOn = useCallback(() => {
        if (!title || !description || !price || !quantity) {
          toast.show({
            title: "Create Add-On Error",
            content: "All fields are required",
            duration: 10000,
            type: "fail"
          });

          return;
        }

        apiService.createEventAddOn(props.event._id, {
            scope: Constants.add_on_scope.event,
            title,
            description,
            fee: parseFloat(price),
            available_quantity: parseInt(quantity, 10)
        }).then(() => {
            toggle();
            getEventAddOns(props.event._id);
        });
    }, [props.event._id, title, description, price, quantity, toggle, getEventAddOns]);

    const deleteAddOn = useCallback((addOnId: string) => {
        apiService.deleteEventAddOn(props.event._id, addOnId).then(() => {
            getEventAddOns(props.event._id);
            toast.show({
                title: "Delete Add-On",
                content: "Add-On deleted successfully",
                duration: 5000,
                type: "success"
            });
        }).catch((error) => {
            toast.show({
                title: "Delete Add-On Error",
                content: error.message,
                duration: 10000,
                type: "fail"
            });
        });
    }, [props.event._id, getEventAddOns]);

    let colDefsPurchases:ColDef<IRowPurchases>[] = [
        { field: "title", minWidth: 120, filter: "agTextColumnFilter" },
        { field: "name", minWidth: 180, filter: "agTextColumnFilter" },
        { field: "quantity", minWidth: 120, filter:true },
        { field: "total_amount", headerName: "Total Amount", minWidth: 120, filter:true },
        { field: "currency", minWidth: 120, filter:true }
    ];

    let colDefs:ColDef<IRow>[] = [
        { field: "title", minWidth: 120, filter: "agTextColumnFilter" },
        { field: "description", minWidth: 180, filter: "agTextColumnFilter" },
        { field: "price", headerName: "Price", minWidth: 120, filter:true },
        { field: "quantity", minWidth: 150, filter:true },
        { field: "remaining", minWidth: 150, filter:true },
        { field: "received", minWidth: 150, filter: true },
        {
            headerName: "",
            field: "actions",
            maxWidth: 50,
            minWidth: 50,
            cellRenderer: ActionsRenderer,
            pinned: "right",
            cellRendererParams: {
                deleteAddOn: (addOnId: string) => { deleteAddOn(addOnId); },
                getEventAddOns: () => { getEventAddOns(props.event._id) }
            },
            resizable: false,
            cellStyle: {
            "overflow": "visible",
            "zIndex": "2"
            }
        }
    ];

    const [rowData, setRowData] = useState<IRow[]>([]);
    const [rowPurchasesData, setRowPurchasesData] = useState<IRowPurchases[]>([]);

    const defaultColDef: ColDef = {
        flex: 1
    };

    const getModalContent = () => {
        return (
            <form onSubmit={(e) => { e.preventDefault(); }} className="createAddOn">
                <div className="title">Create Add-On</div>
                <div className="content">
                    <Input
                        label="Add-On Title"
                        placeholder="Enter add-on title"
                        name="title"
                        required={true}
                        defaultValue={title}
                        onChange={(e) => setTitle(e.target.value)}
                    />
                    <Input
                        label="Add-On Description"
                        placeholder="Enter add-on description"
                        name="description"
                        required={true}
                        defaultValue={description}
                        onChange={(e) => setDescription(e.target.value)}
                    />
                    <Input
                        label="Price"
                        placeholder="How much does it cost?"
                        name="price"
                        type="number"
                        required={true}
                        defaultValue={price}
                        onChange={(e) => setPrice(e.target.value)}
                    />
                    <Input
                        label="Quantity"
                        placeholder="How many available?"
                        name="quantity"
                        type="number"
                        required={true}
                        defaultValue={quantity}
                        onChange={(e) => setQuantity(e.target.value)}
                    />
                </div>
                <div className="actions">
                    <Button
                        onClick={toggle}
                        variant="secondary"
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        onClick={() => {
                            createAddOn();
                        }}
                    >
                        Create Add-On
                    </Button>
                </div>
            </form>
        );
    };

    useEffect(() => {
        if (props.currentTab === props.tabIndex) {
            getEventAddOns(props.event._id);
            getEventAddonPurchases(props.event._id);
        }
    }, [props.currentTab, props.tabIndex, props.event._id, getEventAddOns, getEventAddonPurchases]);

    useEffect(() => {
        if (!loaded) {
            getEventAddonPurchases(props.event._id);
            getEventAddOns(props.event._id);
            setLoaded(true);
        }
    }, [loaded, getEventAddOns, getEventAddonPurchases, props.event._id]);

    return (
    <div className={styles.AddOnsTab}>
        <TabContent className="addOnsSubTabs" activeTab={activeTab} setTab={setTab} tabs={[
            { name: "Add-Ons", actions: [{ label: "Create Add-On", onClick: () => { toggle(); } }] },
            { name: "Add-On Sales", actions: [{ label: "Create Add-On", onClick: () => { toggle(); } }] }
        ]}>
            <div className={styles.AddOnSubTab}>
                <div className={styles.tableWrap}>
                    <AgGridReact
                        rowData={rowData}
                        suppressRowTransform={true}
                        columnDefs={colDefs}
                        defaultColDef={defaultColDef}
                        theme={theme}
                        pagination={true}
                        paginationPageSize={20}
                    />
                </div>
            </div>
            <div className={styles.AddOnSubTab}>
                <div className={styles.tableWrap}>
                    <AgGridReact
                        rowData={rowPurchasesData}
                        suppressRowTransform={true}
                        columnDefs={colDefsPurchases}
                        defaultColDef={defaultColDef}
                        theme={theme}
                        pagination={true}
                        paginationPageSize={20}
                    />
                </div>
            </div>
        </TabContent>
        <Modal className="createAddOn" isShown={isShown} hide={toggle} modalContent={getModalContent()}  />
    </div>
    );
};

export default AddOnsTab;